import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logCrud } from "@/services/activities";

interface KB {
  id: string;
  tenant_id: string;
  slug: string;
  nome: string;
  descricao: string | null;
  status: string;
}

export default function KnowledgeBases() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState<KB[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<KB | null>(null);
  const [form, setForm] = useState<Partial<KB>>({ status: "ativo" });
  const canManage = useMemo(() => !!tenantId, [tenantId]);

  useEffect(() => {
    const load = async () => {
      if (!tenantId) return;
      const { data, error } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId);
      if (error) {
        toast("Erro ao carregar bases");
      } else {
        setItems((data as any[]) || []);
      }
    };
    load();
  }, [tenantId]);

  const save = async () => {
    if (!tenantId) return;
    const payload = {
      tenant_id: tenantId,
      slug: form.slug,
      nome: form.nome,
      descricao: form.descricao,
      status: form.status,
    } as any;
    if (editing) {
      const { error } = await supabase.from("knowledge_bases" as any).update(payload).eq("id", editing.id);
      if (error) {
        toast("Erro ao atualizar");
        return;
      }
      void logCrud({ entitySlug: "entity:knowledge_bases", operationSlug: "update", entityId: editing.id, entityName: form.nome || editing.nome, screenSlug: "configuracoes/rag-bases" });
    } else {
      const { data, error } = await supabase.from("knowledge_bases" as any).insert(payload).select("id").single();
      if (error) {
        toast("Erro ao criar");
        return;
      }
      if (data?.id) {
        void logCrud({ entitySlug: "entity:knowledge_bases", operationSlug: "create", entityId: data.id, entityName: form.nome || "", screenSlug: "configuracoes/rag-bases" });
      }
    }
    const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId);
    setItems((data as any[]) || []);
    setOpen(false);
    setEditing(null);
    setForm({ status: "ativo" });
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Nenhum tenant selecionado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Bases de Conhecimento</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm({ status: "ativo" }); }}>Nova Base</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Editar Base" : "Criar Base"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-2">
                <Input placeholder="Slug" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                <Input placeholder="Nome" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                <Textarea placeholder="Descrição" value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">ativo</SelectItem>
                    <SelectItem value="inativo">inativo</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={save}>Salvar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.slug}</TableCell>
                  <TableCell>{it.nome}</TableCell>
                  <TableCell>{it.status}</TableCell>
                  <TableCell>
                    <Button variant="secondary" onClick={() => { setEditing(it); setForm(it); setOpen(true); }}>Editar</Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">Nenhuma base cadastrada</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
